import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { canClearUp } from "../lib/requests.ts";
import { venueApi, type VenueApi } from "../lib/venueApi.ts";
import type { Venue } from "../lib/venues.ts";
import VenueEditor from "./VenueEditor.tsx";
import { readVenueRecovery } from "../lib/venueRecovery.ts";
export type { Venue } from "../lib/venues.ts";
const noEditingChange = () => {};

// Observers retain their existing picker. Full details and editing are admin
// screens; the database also checks access on both editor RPCs.
export default function Venues({ onPick, canCreate, ownerId, onEditingChange = noEditingChange, api = venueApi }: {
  onPick: (v: Venue) => void; canCreate: boolean; ownerId?: string; onEditingChange?: (editing: boolean) => void; api?: VenueApi;
}) {
  const [venues, setVenues] = useState<Venue[] | null>(null);
  const [loadErr, setLoadErr] = useState<{ message: string; retry: boolean } | null>(null);
  const [trying, setTrying] = useState(false);
  const inFlight = useRef(false);
  const again = useRef(false);
  const [query, setQuery] = useState("");
  // undefined: list, null: new venue, string: existing venue details.
  const [selected, setSelected] = useState<string | null | undefined>(() => {
    const recovered = canCreate ? readVenueRecovery(ownerId) : null;
    return recovered ? recovered.selected : undefined;
  });
  const loadOnce = useCallback(async () => {
    try {
      setVenues(await api.list()); setLoadErr(null);
    } catch (e) {
      const error = e && typeof e === "object" && "message" in e ? e as { message: string; code?: string; status?: number } : { message: String(e) };
      const retry = canClearUp(error, error.status);
      setLoadErr({ message: retry ? "Could not load venues. Check your signal and try again." : error.message, retry });
    }
  }, [api]);
  const load = useCallback(async () => {
    if (inFlight.current) { again.current = true; return; }
    inFlight.current = true; setTrying(true);
    try { do { again.current = false; await loadOnce(); } while (again.current); }
    finally { inFlight.current = false; setTrying(false); }
  }, [loadOnce]);
  useEffect(() => { void load(); }, [load]);
  const retrying = loadErr?.retry === true;
  useEffect(() => {
    if (!retrying) return;
    const tryNow = () => { void load(); };
    const onVisible = () => { if (document.visibilityState === "visible") tryNow(); };
    const timer = setInterval(tryNow, 15000);
    addEventListener("online", tryNow); document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(timer); removeEventListener("online", tryNow); document.removeEventListener("visibilitychange", onVisible); };
  }, [retrying, load]);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return venues?.filter((v) => (canCreate ? [v.name,v.city ?? "",v.country ?? ""] : [v.name]).some((s) => s.toLowerCase().includes(q))) ?? null;
  }, [venues, query, canCreate]);

  if (canCreate && selected !== undefined) return <VenueEditor key={selected ?? "new"} id={selected} ownerId={ownerId} api={api}
    onBack={() => setSelected(undefined)} onPick={onPick} onSaved={() => void load()} onEditingChange={onEditingChange} />;

  return <div className="pad">
    <div className="venue-heading"><div><h2>Venues</h2><p className="muted">{canCreate ? "Manage venue details or start an observation." : "Pick where you are observing."}</p></div>
      {canCreate && <button className="primary" onClick={() => setSelected(null)}>Add a venue</button>}
    </div>
    {loadErr && <div className="empty"><p className="err" role="alert">{loadErr.message}</p><button className="primary" onClick={() => void load()}>Try again</button><p className="muted" style={{ visibility: trying ? "visible" : "hidden" }}>Trying again…</p></div>}
    {venues === null ? !loadErr && <p className="muted">Loading venues…</p> : venues.length === 0 ? <div className="empty"><p><strong>No venues yet.</strong></p><p className="muted">{canCreate ? "Add the first venue to get started." : "An admin adds venues. Check back once they have."}</p></div> : <>
      <label className="search"><span className="sr-only">Search venues</span><input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={canCreate ? "Search by name, city or country" : "Search by venue name"} autoComplete="off" /></label>
      <p className="muted venue-list-count">{shown?.length} of {venues.length} venues</p>
      {shown?.length === 0 ? <p className="muted">No venue matches “{query.trim()}”.</p> : <ul className="list">{shown?.map((v) => <li key={v.id} className={canCreate ? "venue-list-item" : undefined}>
        <button className="row" onClick={() => canCreate ? setSelected(v.id) : onPick(v)}>
          <span>{v.name}</span>{canCreate && <small>{[v.city ?? "No city",v.country].filter(Boolean).join(", ")} · {v.timezone}</small>}
          {canCreate && <small className="venue-detail-link">View details →</small>}
        </button>
      </li>)}</ul>}
    </>}
  </div>;
}
